import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadModules } from 'esri-loader';
import Terraformer from 'terraformer-arcgis-parser';
import MapSettings from 'components/Map/settings';
import { removePin } from 'components/Map/helper';
import { selectBaseMap } from 'redux/app/selectors';
import { getBaseMapsStart } from 'redux/map/actions';
import { LandParcelType } from 'types';
import {
  resolveBasemapConfigForInit,
  buildBasemapFromConfig,
  findBasemapByIdentifier,
  BasemapInitConfig
} from 'utilities/basemap-migration.util';
import { debugLog, debugWarn } from 'utilities/basemap-debug.util';
import {
  validateAndProcessDrawing,
  computeLiveArea,
  DrawingValidationError,
  assertCumulativeAreaWithinLimit,
  assertDrawFeatureCountWithinLimit,
  ArcGISGeometryModules
} from 'utilities/geometry-validation.util';
import {
  MAX_CUMULATIVE_DRAW_AREA_KM2,
  MAX_DRAW_FEATURE_COUNT
} from '../constants/geometryLimits';
import {
  registerMapInstance,
  unregisterMapInstance
} from 'utilities/map-instance.util';
import {
  setupMemberLookupMapUi,
  teardownMemberLookupMapUi
} from '../utils/memberLookupMapUiControls';

export type DrawingTool = 'polygon' | 'polyline' | 'circle' | 'selector';

export interface DrawingResult {
  polygon: GeoJSON.GeometryObject;
  area: number;
  centroid: { latitude: number; longitude: number };
}

interface UseMemberLookupMapOptions {
  containerRef: React.RefObject<HTMLDivElement>;
  /**
   * Called each time a drawing is completed and validated.
   * Previously drawn polygons remain visible on the layer.
   */
  onDrawingComplete: (result: DrawingResult) => void;
  /** Called when a validation error occurs (self-intersection, too large, etc.) */
  onDrawingError?: (error: DrawingValidationError) => void;
  /**
   * Called when the user deletes a graphic from the map (e.g. select + Delete key).
   * Receives the index of the shape in the parent's drawnShapes array so the
   * caller can remove it from state and keep state in sync with the map layer.
   */
  onShapeDeleted?: (index: number) => void;
  /**
   * Called when the user finishes editing an existing shape (e.g. resize vertices).
   * Receives the shape index and the re-validated result so the caller can update
   * drawnShapes in place and keep Total Area in sync with the map.
   */
  onShapeUpdated?: (index: number, result: DrawingResult) => void;
  /**
   * Shapes already stored in parent state. Re-applied to the layer after remount
   * (e.g. when the user navigates Back from Payment to Area input).
   */
  existingShapes?: DrawingResult[];
}

interface MemberLookupMapModules {
  Map: any;
  MapView: any;
  GraphicsLayer: any;
  SketchViewModel: any;
  Extent: any;
  SpatialReference: any;
  WebMercatorUtils: any;
  GeometryEngine: any;
  Graphic: any;
  Config: any;
  Basemap: any;
  BasemapStyle: any;
  WebMap: any;
  Polygon: any;
}

interface UseMemberLookupMapReturn {
  isReady: boolean;
  isMapLoading: boolean;
  isDrawing: boolean;
  activeTool: DrawingTool;
  liveArea: number | undefined;
  /** The ArcGIS MapView instance — exposed so DrawingTooltip can attach to it */
  view: any;
  /** The SketchViewModel instance — exposed so DrawingTooltip can listen to events */
  sketchViewModel: any;
  startDrawing: (tool: DrawingTool) => void;
  cancelDrawing: () => void;
  /**
   * Cancels any in-progress sketch and clears all graphics from the drawing layer.
   * The consumer is responsible for clearing its own polygon state.
   */
  clearDrawing: () => void;
  /** Adds a cadastral parcel boundary from address search as a validated shape */
  addLandParcel: (parcel: LandParcelType) => void;
}

/**
 * Creates an isolated ArcGIS MapView + SketchViewModel for the member lookup drawing step.
 *
 * WHY isolated (not reusing window.loadedSketchViewModel):
 * - The global map singleton is owned by the home page and persists across navigation.
 *   Hijacking it from inside a wizard step would corrupt home-page drawing state.
 *
 * Supports polygon, polyline, and circle tools with the same validation rules
 * and UX feedback as the lodge-enquiry flow (live area, geometry validation).
 *
 * HOW: loadModules from esri-loader (ArcGIS JS API 4.33 already bootstrapped in index.tsx).
 * On unmount the MapView and Map are destroyed so no references leak.
 */
function useMemberLookupMap({
  containerRef,
  onDrawingComplete,
  onDrawingError,
  onShapeDeleted,
  onShapeUpdated,
  existingShapes = []
}: UseMemberLookupMapOptions): UseMemberLookupMapReturn {
  const [isReady, setIsReady] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  const [activeTool, setActiveTool] = useState<DrawingTool>('selector');
  const [liveArea, setLiveArea] = useState<number | undefined>(undefined);
  // Stateful copies of the ArcGIS instances so that consumers (e.g. tooltip)
  // re-render once the map is initialised, not just on ref assignment.
  const [viewState, setViewState] = useState<any>(null);
  const [sketchState, setSketchState] = useState<any>(null);

  const sketchRef = useRef<any>(null);
  const drawingLayerRef = useRef<any>(null);
  const viewRef = useRef<any>(null);
  const mapRef = useRef<any>(null);
  const modulesRef = useRef<ArcGISGeometryModules | null>(null);
  const isMountedRef = useRef(true);

  const onDrawingCompleteRef = useRef(onDrawingComplete);
  const onDrawingErrorRef = useRef(onDrawingError);
  const onShapeDeletedRef = useRef(onShapeDeleted);
  const onShapeUpdatedRef = useRef(onShapeUpdated);
  const existingShapesRef = useRef(existingShapes);
  const activeToolRef = useRef<DrawingTool>('selector');
  // Maps each completed graphic to its index in the parent's drawnShapes array
  // so that delete events can be translated to the correct array index.
  const graphicIndexMapRef = useRef<Map<any, number>>(new Map());
  const shapeCounterRef = useRef(0);
  const mapModulesRef = useRef<MemberLookupMapModules | null>(null);
  const mapInitializedRef = useRef(false);
  const mapInitGenerationRef = useRef(0);
  const initialBasemapAppliedRef = useRef(false);
  const initBasemapConfigRef = useRef<BasemapInitConfig | null>(null);

  const baseMap = useSelector(selectBaseMap);
  const baseMapRef = useRef(baseMap);
  baseMapRef.current = baseMap;
  const basemapOptions = useSelector(
    (state: any) => state.mapCache?.basemapOptions
  );
  const basemapError = useSelector(
    (state: any) => state.mapCache?.basemapError
  );
  const dispatch = useDispatch();

  useEffect(() => {
    onDrawingCompleteRef.current = onDrawingComplete;
  }, [onDrawingComplete]);

  useEffect(() => {
    onDrawingErrorRef.current = onDrawingError;
  }, [onDrawingError]);

  useEffect(() => {
    onShapeDeletedRef.current = onShapeDeleted;
  }, [onShapeDeleted]);

  useEffect(() => {
    onShapeUpdatedRef.current = onShapeUpdated;
  }, [onShapeUpdated]);

  useEffect(() => {
    existingShapesRef.current = existingShapes;
  }, [existingShapes]);

  /**
   * Re-adds graphics from parent state after the map remounts with an empty layer.
   *
   * WHY: Step navigation destroys the MapView; drawnShapes in wizard state survives.
   * HOW: Only restores when the layer has no graphics to avoid duplicating live drawings.
   */
  useEffect(() => {
    if (!isReady || existingShapes.length === 0) return;

    const drawingLayer = drawingLayerRef.current;
    const modules = modulesRef.current;
    const view = viewRef.current;
    if (!drawingLayer || !modules || !view) return;
    if (drawingLayer.graphics.length > 0) return;

    const { Graphic, WebMercatorUtils } = modules;
    graphicIndexMapRef.current.clear();
    shapeCounterRef.current = 0;

    existingShapes.forEach((shape, index) => {
      const geometry: any = Terraformer.convert(shape.polygon);
      const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
        geometry
      );
      const graphic = new Graphic({
        geometry: {
          ...webMercatorGeometry,
          type: 'polygon'
        },
        symbol: MapSettings.polygonSymbol
      });
      drawingLayer.add(graphic);
      graphicIndexMapRef.current.set(graphic, index);
      shapeCounterRef.current = index + 1;
    });

    const graphics = drawingLayer.graphics.toArray();
    if (graphics.length > 0) {
      view.goTo(graphics.map((g: any) => g.geometry)).catch((error: any) => {
        if (error.name !== 'AbortError') {
          console.warn('Error zooming to restored shapes:', error);
        }
      });
    }
  }, [isReady, existingShapes]);

  /**
   * Tear down the ArcGIS map only when the drawing step unmounts.
   *
   * WHY: basemapOptions loading must not destroy the MapView — that caused a full
   * remount a few seconds after first paint and dropped the active SketchViewModel.
   */
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      mapInitGenerationRef.current += 1;

      if (!mapInitializedRef.current) return;

      mapInitializedRef.current = false;
      initialBasemapAppliedRef.current = false;
      initBasemapConfigRef.current = null;
      setIsReady(false);
      setIsMapLoading(true);
      setIsDrawing(false);
      setViewState(null);
      setSketchState(null);

      teardownMemberLookupMapUi();
      unregisterMapInstance();

      if (sketchRef.current) {
        sketchRef.current.cancel();
        sketchRef.current.destroy();
        sketchRef.current = null;
      }
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
      drawingLayerRef.current = null;
      modulesRef.current = null;
      mapModulesRef.current = null;
    };
  }, [containerRef]);

  /**
   * Initialise the ArcGIS map once basemap config can be resolved.
   *
   * WHY: Applying basemap after MapView creation caused a visible flash from the
   * hardcoded default to the persisted Redux style.
   *
   * HOW: Waits for basemapOptions when a state style has no static fallback,
   * builds the correct basemap before creating the MapView, then marks ready.
   * This effect intentionally has no destroy cleanup — see unmount effect above.
   */
  useEffect(() => {
    if (!containerRef.current || mapInitializedRef.current) return;

    const basemapConfig = resolveBasemapConfigForInit(
      baseMapRef.current,
      basemapOptions || []
    );

    if (!basemapConfig) {
      setIsMapLoading(true);
      setIsReady(false);
      if (
        (!basemapOptions || basemapOptions.length === 0) &&
        basemapError === ''
      ) {
        dispatch(getBaseMapsStart());
      }
      return;
    }

    mapInitializedRef.current = true;
    initBasemapConfigRef.current = basemapConfig;

    const basemapItem = findBasemapByIdentifier(
      baseMapRef.current,
      basemapOptions || []
    );
    if (basemapItem) {
      initialBasemapAppliedRef.current = true;
    }

    const initGeneration = ++mapInitGenerationRef.current;
    setIsMapLoading(true);
    setIsReady(false);

    const container = containerRef.current;

    debugLog('[Member Lookup Map] Initialising with basemap config:', {
      baseMap: baseMapRef.current,
      styleId: basemapConfig.styleId,
      webmapId: basemapConfig.webmapId
    });

    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/layers/GraphicsLayer',
      'esri/widgets/Sketch/SketchViewModel',
      'esri/geometry/Extent',
      'esri/geometry/SpatialReference',
      'esri/geometry/support/webMercatorUtils',
      'esri/geometry/geometryEngine',
      'esri/Graphic',
      'esri/config',
      'esri/Basemap',
      'esri/support/BasemapStyle',
      'esri/WebMap',
      'esri/geometry/Polygon'
    ]).then(
      async ([
        Map,
        MapView,
        GraphicsLayer,
        SketchViewModel,
        Extent,
        SpatialReference,
        WebMercatorUtils,
        GeometryEngine,
        Graphic,
        Config,
        Basemap,
        BasemapStyle,
        WebMap,
        Polygon
      ]: any[]) => {
        if (
          !isMountedRef.current ||
          initGeneration !== mapInitGenerationRef.current
        ) {
          return;
        }

        Config.apiKey = process.env.REACT_APP_ESRI_API_KEY;

        let basemapToUse;
        try {
          basemapToUse = await buildBasemapFromConfig(
            basemapConfig,
            Basemap,
            BasemapStyle
          );
        } catch (error) {
          if (
            !isMountedRef.current ||
            initGeneration !== mapInitGenerationRef.current
          ) {
            return;
          }
          debugWarn('[Member Lookup Map] Basemap load failed:', error);
          basemapToUse = new Basemap({
            style: new BasemapStyle({ id: 'arcgis/imagery' })
          });
        }

        if (
          !isMountedRef.current ||
          initGeneration !== mapInitGenerationRef.current
        ) {
          return;
        }

        modulesRef.current = { GeometryEngine, WebMercatorUtils, Graphic };

        const drawingLayer = new GraphicsLayer({
          id: 'drawing-layer'
        });
        drawingLayerRef.current = drawingLayer;

        const ausExtent = new Extent({
          ...MapSettings.AUS_Extent,
          spatialReference: new SpatialReference({ wkid: MapSettings.wkid })
        });

        const mapModules: MemberLookupMapModules = {
          Map,
          MapView,
          GraphicsLayer,
          SketchViewModel,
          Extent,
          SpatialReference,
          WebMercatorUtils,
          GeometryEngine,
          Graphic,
          Config,
          Basemap,
          BasemapStyle,
          WebMap,
          Polygon
        };
        mapModulesRef.current = mapModules;

        const map = new Map({
          basemap: basemapToUse,
          layers: [drawingLayer]
        });
        mapRef.current = map;

        const view = new MapView({
          container,
          map,
          extent: ausExtent,
          constraints: {
            maxZoom: 20,
            geometry: new Extent(MapSettings.AUS_Boundary_Constraint),
            minScale: MapSettings.mapMinScale
          },
          ui: { padding: 0 }
        });
        viewRef.current = view;
        setViewState(view);

        registerMapInstance(map, view, mapModules);

        setupMemberLookupMapUi(view);

        // Symbol config matches the shared Map component for visual consistency
        const sketch = new SketchViewModel({
          view,
          layer: drawingLayer,
          polygonSymbol: MapSettings.polygonSymbol,
          activeLineSymbol: {
            type: 'simple-line',
            style: 'short-dash',
            color: '#002d9c',
            width: 1
          },
          activeVertexSymbol: {
            type: 'simple-marker',
            style: 'circle',
            size: 3,
            color: '#002d9c',
            outline: { style: 'none' }
          },
          activeFillSymbol: {
            type: 'simple-fill',
            style: 'solid',
            color: 'rgba(82, 82, 82, 0.15)',
            outline: { style: 'none' }
          },
          polylineSymbol: {
            type: 'simple-line',
            style: 'solid',
            color: '#002d9c',
            width: 1
          },
          vertexSymbol: {
            type: 'simple-marker',
            style: 'circle',
            size: 3,
            color: '#002d9c',
            outline: { style: 'none' }
          }
        });
        sketchRef.current = sketch;
        setSketchState(sketch);

        sketch.on('create', (event: __esri.SketchViewModelCreateEvent) => {
          if (event.state === 'active') {
            setIsDrawing(true);
            // Live area/length feedback while drawing
            if (event.graphic?.geometry && modulesRef.current) {
              const area = computeLiveArea(
                event.graphic.geometry,
                modulesRef.current.GeometryEngine,
                modulesRef.current.WebMercatorUtils
              );
              if (area > 0) setLiveArea(area);
            }
          }

          if (event.state === 'complete') {
            setIsDrawing(false);
            setLiveArea(undefined);

            const graphic = event.graphic;
            if (!graphic?.geometry) return;

            try {
              const modules = modulesRef.current;
              if (!modules) return;

              assertDrawFeatureCountWithinLimit(
                existingShapesRef.current.length,
                MAX_DRAW_FEATURE_COUNT
              );

              // No area cap for member lookup — pass Infinity so the 20km² lodge-enquiry
              // limit is skipped while all other validations still apply.
              const result = validateAndProcessDrawing(
                graphic,
                activeToolRef.current,
                'light',
                modules,
                Infinity
              );

              const existingAreaKm2 = existingShapesRef.current.reduce(
                (sum, shape) => sum + shape.area,
                0
              );
              assertCumulativeAreaWithinLimit(
                existingAreaKm2,
                result.geometryArea,
                MAX_CUMULATIVE_DRAW_AREA_KM2
              );

              // Replace the raw sketch graphic with the validated/buffered one
              drawingLayer.remove(graphic);
              drawingLayer.add(result.graphic);

              // Register this graphic so delete events can map back to the
              // correct index in the parent's drawnShapes array.
              const shapeIndex = shapeCounterRef.current;
              graphicIndexMapRef.current.set(result.graphic, shapeIndex);
              shapeCounterRef.current += 1;

              onDrawingCompleteRef.current({
                polygon: result.polygonInGeoJson,
                area: result.geometryArea,
                centroid: result.centroid
              });
            } catch (e) {
              drawingLayer.remove(graphic);
              if (
                e instanceof DrawingValidationError &&
                onDrawingErrorRef.current
              ) {
                onDrawingErrorRef.current(e);
              }
            }

            setActiveTool('selector');
            activeToolRef.current = 'selector';
          }

          if (event.state === 'cancel') {
            setIsDrawing(false);
            setLiveArea(undefined);
          }
        });

        /**
         * WHY: Resizing/editing an existing polygon fires `update` events, not `create`.
         * Without this handler drawnShapes keeps the original area while the map graphic
         * reflects the new geometry — Total Area and backend registration stay stale.
         *
         * HOW: On complete, re-validate each edited graphic and call onShapeUpdated with
         * its index from graphicIndexMapRef. On validation failure, restore the previous
         * geometry from existingShapesRef so map and state remain aligned.
         */
        sketch.on('update', (event: __esri.SketchViewModelUpdateEvent) => {
          if (event.state === 'start' || event.state === 'active') {
            setIsDrawing(true);
            const graphic = event.graphics[0];
            if (graphic?.geometry && modulesRef.current) {
              const area = computeLiveArea(
                graphic.geometry,
                modulesRef.current.GeometryEngine,
                modulesRef.current.WebMercatorUtils
              );
              if (area > 0) setLiveArea(area);
            }
          }

          if (event.state === 'complete') {
            setIsDrawing(false);
            setLiveArea(undefined);

            const modules = modulesRef.current;
            if (!modules || !onShapeUpdatedRef.current) return;

            event.graphics.forEach((graphic) => {
              const shapeIndex = graphicIndexMapRef.current.get(graphic);
              if (shapeIndex === undefined || !graphic?.geometry) return;

              const originalShape = existingShapesRef.current[shapeIndex];

              try {
                const result = validateAndProcessDrawing(
                  graphic,
                  'polygon',
                  'light',
                  modules,
                  Infinity
                );

                const existingAreaKm2 = existingShapesRef.current.reduce(
                  (sum, shape, i) =>
                    i === shapeIndex ? sum : sum + shape.area,
                  0
                );
                assertCumulativeAreaWithinLimit(
                  existingAreaKm2,
                  result.geometryArea,
                  MAX_CUMULATIVE_DRAW_AREA_KM2
                );

                drawingLayer.remove(graphic);
                drawingLayer.add(result.graphic);
                graphicIndexMapRef.current.delete(graphic);
                graphicIndexMapRef.current.set(result.graphic, shapeIndex);

                onShapeUpdatedRef.current?.(shapeIndex, {
                  polygon: result.polygonInGeoJson,
                  area: result.geometryArea,
                  centroid: result.centroid
                });
              } catch (e) {
                drawingLayer.remove(graphic);

                if (originalShape) {
                  const { Graphic, WebMercatorUtils } = modules;
                  const geometry: any = Terraformer.convert(
                    originalShape.polygon
                  );
                  const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
                    geometry
                  );
                  const restoredGraphic = new Graphic({
                    geometry: {
                      ...webMercatorGeometry,
                      type: 'polygon'
                    },
                    symbol: MapSettings.polygonSymbol
                  });
                  drawingLayer.add(restoredGraphic);
                  graphicIndexMapRef.current.set(restoredGraphic, shapeIndex);
                }

                if (
                  e instanceof DrawingValidationError &&
                  onDrawingErrorRef.current
                ) {
                  onDrawingErrorRef.current(e);
                }
              }
            });
          }
        });

        /**
         * WHY: ArcGIS SketchViewModel natively lets users select a graphic and
         * press the Delete key to remove it from the layer. Without handling
         * this event the parent's drawnShapes array would remain stale and the
         * deleted polygon would still be included in the backend submission.
         *
         * HOW: Each validated graphic is stored in graphicIndexMapRef alongside
         * the array index it was added at. On delete we resolve the indices,
         * sort descending, and call onShapeDeleted once per index so the parent
         * can splice each one out correctly.
         */
        sketch.on('delete', (event: { graphics: __esri.Graphic[] }) => {
          if (!onShapeDeletedRef.current) return;

          const indices: number[] = [];
          event.graphics.forEach((graphic) => {
            const index = graphicIndexMapRef.current.get(graphic);
            if (index !== undefined) {
              indices.push(index);
              graphicIndexMapRef.current.delete(graphic);
            }
          });

          // Remove highest indices first to avoid shifting issues on the
          // parent's array when multiple shapes are deleted at once.
          indices
            .sort((a, b) => b - a)
            .forEach((index) => onShapeDeletedRef.current?.(index));
        });

        view.when(() => {
          if (
            isMountedRef.current &&
            initGeneration === mapInitGenerationRef.current
          ) {
            setIsMapLoading(false);
            setIsReady(true);
          }
        });
      }
    );
  }, [basemapOptions, basemapError, dispatch, containerRef]);

  /**
   * Applies the feature-service basemap in place when the map was first created
   * from a static national fallback (before basemapOptions arrived).
   *
   * WHY: Mirrors components/Map/index.tsx — swap basemap or WebMap without
   * destroying the MapView or SketchViewModel so drawing tools stay active.
   */
  useEffect(() => {
    if (
      !isReady ||
      !basemapOptions?.length ||
      initialBasemapAppliedRef.current
    ) {
      return;
    }

    const view = viewRef.current;
    const map = mapRef.current;
    const mapModules = mapModulesRef.current;
    if (!view || !map || !mapModules) return;

    const basemapItem = findBasemapByIdentifier(
      baseMapRef.current,
      basemapOptions
    );
    if (!basemapItem) {
      initialBasemapAppliedRef.current = true;
      return;
    }

    const config = {
      styleId: basemapItem.styleId,
      webmapId: basemapItem.webmapId
    };
    const initConfig = initBasemapConfigRef.current;

    if (
      initConfig?.styleId === config.styleId &&
      !config.webmapId &&
      !initConfig?.webmapId
    ) {
      debugLog(
        '[Member Lookup Map] Feature-service basemap matches init config — skipping update'
      );
      initialBasemapAppliedRef.current = true;
      return;
    }

    initialBasemapAppliedRef.current = true;

    const { Basemap, BasemapStyle, WebMap } = mapModules;

    debugLog('[Member Lookup Map] Applying feature-service basemap in place:', {
      baseMap: baseMapRef.current,
      styleId: config.styleId,
      webmapId: config.webmapId
    });

    const preserveDrawingLayer = (targetMap: any) => {
      const drawingLayer = drawingLayerRef.current;
      if (drawingLayer && !targetMap.findLayerById('drawing-layer')) {
        targetMap.add(drawingLayer);
      }
    };

    if (config.styleId && config.webmapId) {
      const webMapInstance = new WebMap({
        portalItem: { id: config.webmapId }
      });
      webMapInstance
        .loadAll()
        .then(() => {
          if (!isMountedRef.current) return;

          webMapInstance.basemap = new Basemap({
            style: new BasemapStyle({ id: config.styleId })
          });
          preserveDrawingLayer(webMapInstance);

          view.map = webMapInstance;
          mapRef.current = webMapInstance;
          registerMapInstance(webMapInstance, view, mapModules);

          debugLog('[Member Lookup Map] Dual-load basemap applied in place');
        })
        .catch((error: any) => {
          if (!isMountedRef.current) return;
          debugWarn('[Member Lookup Map] WebMap dual-load failed:', error);
        });
      return;
    }

    if (config.styleId) {
      map.basemap = new Basemap({
        style: new BasemapStyle({ id: config.styleId })
      });
      return;
    }

    if (config.webmapId) {
      const webmapBasemap = new Basemap({
        portalItem: { id: config.webmapId }
      });
      webmapBasemap
        .load()
        .then(() => {
          if (!isMountedRef.current) return;
          map.basemap = webmapBasemap;
        })
        .catch((error: any) => {
          if (!isMountedRef.current) return;
          debugWarn('[Member Lookup Map] Webmap basemap load failed:', error);
          map.basemap = webmapBasemap;
        });
    }
  }, [isReady, basemapOptions, baseMap]);

  const startDrawing = useCallback((tool: DrawingTool) => {
    if (!sketchRef.current || tool === 'selector') return;
    sketchRef.current.cancel();
    setLiveArea(undefined);

    const mode = tool !== 'circle' ? 'click' : 'freehand';
    sketchRef.current.create(tool, { mode });

    setActiveTool(tool);
    activeToolRef.current = tool;
    setIsDrawing(true);
  }, []);

  const cancelDrawing = useCallback(() => {
    if (!sketchRef.current) return;
    sketchRef.current.cancel();
    setActiveTool('selector');
    activeToolRef.current = 'selector';
    setIsDrawing(false);
    setLiveArea(undefined);
  }, []);

  const clearDrawing = useCallback(() => {
    if (!sketchRef.current) return;
    sketchRef.current.cancel();
    drawingLayerRef.current?.removeAll();
    graphicIndexMapRef.current.clear();
    shapeCounterRef.current = 0;
    setActiveTool('selector');
    activeToolRef.current = 'selector';
    setIsDrawing(false);
    setLiveArea(undefined);
  }, []);

  const addLandParcel = useCallback((parcel: LandParcelType) => {
    const drawingLayer = drawingLayerRef.current;
    const modules = modulesRef.current;
    const sketch = sketchRef.current;
    const view = viewRef.current;

    if (!drawingLayer || !modules || !sketch) return;

    sketch.cancel();
    view?.closePopup();
    removePin();
    setIsDrawing(false);
    setLiveArea(undefined);

    if (parcel.shape === 'no shape' || typeof parcel.shape === 'string') {
      onDrawingErrorRef.current?.(
        new DrawingValidationError(
          'Sorry, we did not find a parcel boundary for this address. Please sketch your project area with the drawing tools.'
        )
      );
      return;
    }

    try {
      const { Graphic, WebMercatorUtils } = modules;
      const geometry: any = Terraformer.convert(parcel.shape);
      const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
        geometry
      );

      assertDrawFeatureCountWithinLimit(
        existingShapesRef.current.length,
        MAX_DRAW_FEATURE_COUNT
      );

      const graphic = new Graphic({
        geometry: {
          ...webMercatorGeometry,
          type: 'polygon'
        },
        symbol: MapSettings.polygonSymbol
      });

      const result = validateAndProcessDrawing(
        graphic,
        'polygon',
        'light',
        modules,
        Infinity
      );

      const existingAreaKm2 = existingShapesRef.current.reduce(
        (sum, shape) => sum + shape.area,
        0
      );
      assertCumulativeAreaWithinLimit(
        existingAreaKm2,
        result.geometryArea,
        MAX_CUMULATIVE_DRAW_AREA_KM2
      );

      drawingLayer.add(result.graphic);

      const shapeIndex = shapeCounterRef.current;
      graphicIndexMapRef.current.set(result.graphic, shapeIndex);
      shapeCounterRef.current += 1;

      onDrawingCompleteRef.current({
        polygon: result.polygonInGeoJson,
        area: result.geometryArea,
        centroid: result.centroid
      });

      setActiveTool('selector');
      activeToolRef.current = 'selector';
    } catch (e) {
      if (e instanceof DrawingValidationError && onDrawingErrorRef.current) {
        onDrawingErrorRef.current(e);
      }
    }
  }, []);

  return {
    isReady,
    isMapLoading,
    isDrawing,
    activeTool,
    liveArea,
    view: viewState,
    sketchViewModel: sketchState,
    startDrawing,
    cancelDrawing,
    clearDrawing,
    addLandParcel
  };
}

export default useMemberLookupMap;
